import { ChemicalProduct } from './types';

// This file stores the structured chemical data transcribed from the provided guide.
export const CHEMICALS_DATA: ChemicalProduct[] = [
  // --- ARM & HAMMER ---
  {
    id: 'arm_hammer_baking_soda',
    brand: 'Arm & Hammer',
    productName: 'Baking Soda',
    category: 'Balancers',
    description: 'Pure sodium bicarbonate. The core product used to raise total alkalinity in pool water, which helps buffer and stabilize pH levels.',
    usage: 'To raise TA by 10 ppm in 10,000 gallons, add 1.5 lbs of Baking Soda.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'arm_hammer_ph_boost',
    brand: 'Arm & Hammer',
    productName: 'pH Boost',
    category: 'Balancers',
    description: 'A product in the Arm & Hammer Performance Products line, likely a sodium carbonate (soda ash) based product for raising pH levels.',
    usage: 'Dosing instructions not specified. Used to raise pH.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 } // Standard Soda Ash formula
  },

  // --- HTH ---
  {
    id: 'hth_alkalinity_up',
    brand: 'HTH',
    productName: 'Pool Care Alkalinity Up',
    category: 'Balancers',
    description: 'Granular sodium bicarbonate to raise total alkalinity and buffer pH.',
    usage: 'Add as needed based on water test results to maintain a TA of 80-120 ppm.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 } // Standard Baking Soda
  },
  {
    id: 'hth_ph_up',
    brand: 'HTH',
    productName: 'Pool Care pH Up',
    category: 'Balancers',
    description: 'Granular balancers to raise (sodium carbonate) the pH of pool water.',
    usage: 'Add as needed based on water test results to maintain a pH of 7.2-7.8.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 } // Standard Soda Ash
  },
  {
    id: 'hth_ph_down',
    brand: 'HTH',
    productName: 'Pool Care pH Down',
    category: 'Balancers',
    description: 'Granular balancers to lower (sodium bisulfate) the pH of pool water.',
    usage: 'Add as needed based on water test results.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 16, baseUnit: 'oz', perVolume: 10000, raisesBy: -0.2 } // Generic Sodium Bisulfate
  },
  {
    id: 'hth_calcium_up',
    brand: 'HTH',
    productName: 'Pool Care Calcium Up',
    category: 'Balancers',
    description: 'Granular calcium chloride to raise calcium hardness and protect surfaces.',
    usage: 'Add as needed to maintain a CH of 200-400 ppm.',
    functions: ['RAISE_CH'],
    formula: { baseAmount: 1.2, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 } // Standard Calcium Chloride
  },
  {
    id: 'hth_stabilizer',
    brand: 'HTH',
    productName: 'Pool Care Chlorine Stabilizer',
    category: 'Balancers',
    description: 'Granular cyanuric acid to protect chlorine from sunlight.',
    usage: 'Add as needed to maintain a CYA of 30-50 ppm.',
    functions: ['RAISE_CYA'],
    formula: { baseAmount: 1.3, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 } // Standard CYA
  },
  {
    id: 'hth_shock_ultra',
    brand: 'HTH',
    productName: 'Pool Care Shock Ultra',
    category: 'Shocks & Oxidizers',
    description: 'A powerful, 4-in-1 Cal Hypo-based shock. Kills bacteria and algae, restores clarity. 68% Calcium Hypochlorite.',
    usage: 'Add 1 lb per 10,000 gallons weekly. Broadcast into the deep end of the pool.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 2.35, unit: 'oz' } // Based on 68% Cal-Hypo
  },
  
  // --- Clorox Pool&Spa ---
  {
    id: 'clorox_alkalinity_increaser',
    brand: 'Clorox Pool&Spa',
    productName: 'Alkalinity Increaser',
    category: 'Balancers',
    description: 'A granular product (sodium bicarbonate) used to raise total alkalinity and help buffer pH.',
    usage: 'Add as needed based on water test results to raise TA to the 80-120 ppm range.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'clorox_ph_up',
    brand: 'Clorox Pool&Spa',
    productName: 'pH Up',
    category: 'Balancers',
    description: 'A granular product (sodium carbonate) used to raise low pH levels in pool water.',
    usage: 'Add as needed based on water test results to raise pH to the 7.2-7.6 range.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 }
  },
  {
    id: 'clorox_ph_down',
    brand: 'Clorox Pool&Spa',
    productName: 'pH Down',
    category: 'Balancers',
    description: 'A granular product (sodium bisulfate) used to lower high pH levels in pool water.',
    usage: 'Add as needed based on water test results to lower pH to the 7.2-7.6 range.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 16, baseUnit: 'oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'clorox_calcium_increaser',
    brand: 'Clorox Pool&Spa',
    productName: 'Calcium Hardness Increaser',
    category: 'Balancers',
    description: 'A granular product (calcium chloride) used to raise calcium hardness levels to prevent corrosion and etching.',
    usage: 'Add as needed based on water test results to raise CH to the 200-500 ppm range.',
    functions: ['RAISE_CH'],
    formula: { baseAmount: 1.2, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'clorox_stabilizer',
    brand: 'Clorox Pool&Spa',
    productName: 'Chlorine Stabilizer',
    category: 'Balancers',
    description: 'A granular product (cyanuric acid) that protects chlorine from sunlight degradation, helping it last longer.',
    usage: 'Add as needed based on water test results to maintain CYA levels between 30-50 ppm.',
    functions: ['RAISE_CYA'],
    formula: { baseAmount: 1.3, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'clorox_shock_xtrablue',
    brand: 'Clorox Pool&Spa',
    productName: 'Shock XtraBlue+',
    category: 'Shocks & Oxidizers',
    description: 'A fast-dissolving, multi-functional shock that clears cloudy water, kills bacteria, and fights algae. Contains blue clarifying crystals. 58.2% Sodium Dichloro-s-triazinetrione.',
    usage: 'Typically, 1 lb treats 12,000 gallons. Broadcast into the deep end of the pool with the pump running.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 2.5, unit: 'oz' } // Based on 58% Dichlor
  },

  // --- BioGuard ---
   {
    id: 'bioguard_balance_pak_100',
    brand: 'BioGuard',
    productName: 'Balance PAK 100',
    category: 'Balancers',
    description: 'Raises total alkalinity. A granular sodium bicarbonate product that dissolves quickly.',
    usage: 'Add as needed based on water test results to achieve a TA of 125-150 ppm.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'bioguard_balance_pak_200',
    brand: 'BioGuard',
    productName: 'Balance PAK 200',
    category: 'Balancers',
    description: 'Raises pH. A granular sodium carbonate product that protects equipment from corrosion.',
    usage: 'Add as needed based on water test results to achieve a pH of 7.4-7.6.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 }
  },
  {
    id: 'bioguard_lo_n_slo',
    brand: 'BioGuard',
    productName: 'Lo \'N Slo',
    category: 'Balancers',
    description: 'Lowers pH and total alkalinity. A granular sodium bisulfate product.',
    usage: 'Add as needed based on water test results.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 16, baseUnit: 'oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'bioguard_balance_pak_300',
    brand: 'BioGuard',
    productName: 'Balance PAK 300',
    category: 'Balancers',
    description: 'Raises calcium hardness. A granular calcium chloride product.',
    usage: 'Add as needed to achieve a CH of 175-275 ppm.',
    functions: ['RAISE_CH'],
    formula: { baseAmount: 1.2, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'bioguard_stabilizer_100',
    brand: 'BioGuard',
    productName: 'Stabilizer 100',
    category: 'Balancers',
    description: 'Raises cyanuric acid levels to protect chlorine from sunlight. A granular product.',
    usage: 'Add as needed to achieve a CYA level of 30-100 ppm.',
    functions: ['RAISE_CYA'],
    formula: { baseAmount: 1.3, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'bioguard_smart_shock',
    brand: 'BioGuard',
    productName: 'Smart Shock',
    category: 'Shocks & Oxidizers',
    description: 'A multi-functional shock that kills bacteria and algae, oxidizes, clarifies, and buffers pH. Contains blue algae-fighting crystals. Does not require pre-dissolving.',
    usage: 'Add 1 lb per 12,000 gallons of water weekly.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 2.5, unit: 'oz' } // Dichlor based, estimate
  },
  
  // --- Pool Essentials ---
  {
    id: 'pool_essentials_chlorinating_liquid',
    brand: 'Pool Essentials',
    productName: 'Chlorinating Liquid',
    category: 'Sanitizers',
    description: 'A liquid chlorine (sodium hypochlorite) sanitizer and shock. It is fast-acting, kills bacteria and algae, and is calcium-free.',
    usage: 'For routine maintenance to maintain a 1-4 ppm free chlorine level, add 13 fl. oz. per 10,000 gallons of water.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 13, unit: 'fl_oz' } // Assuming 10% strength
  },

  // --- Champion ---
  {
    id: 'champion_liquid_chlorinator',
    brand: 'Champion',
    productName: 'Pool Shock Liquid Chlorinator',
    category: 'Sanitizers',
    description: 'A 12.5% sodium hypochlorite solution for sanitizing and shocking swimming pools. It is fast-acting, kills bacteria and algae, and is calcium-free.',
    usage: 'To raise free chlorine by 1 ppm, add approximately 10.4 fl. oz. per 10,000 gallons of water.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 10.4, unit: 'fl_oz' }
  },

  // --- HDX ---
  {
    id: 'hdx_muriatic_acid',
    brand: 'HDX',
    productName: 'Swimming Pool Muriatic Acid',
    category: 'Balancers',
    description: 'A powerful liquid acid (hydrochloric acid) used to lower high pH and total alkalinity. Helps prevent scaling caused by high pH and calcium levels.',
    usage: 'Handle with extreme care. Add small amounts to the deep end of the pool.',
    functions: ['LOWER_PH_TA'],
    formula: { 
        amountPerDrop: 12, 
        unitPerDrop: 'fl_oz',
        baseAmount: 16, 
        baseUnit: 'fl_oz',
        perVolume: 10000,
        raisesBy: -0.2
    }
  },

  // --- Generic for Calculations ---
    {
    id: 'generic_muriatic_acid_34_6',
    brand: 'Generic',
    productName: 'Muriatic 34.6% - 22° Baumé',
    category: 'Balancers',
    description: 'Liquid acid for lowering pH/TA. 34.6% strength.',
    usage: 'Handle with extreme care.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 14.5, baseUnit: 'fl_oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'generic_muriatic_acid_31_45',
    brand: 'Generic',
    productName: 'Muriatic 31.45% - 20° Baumé',
    category: 'Balancers',
    description: 'Standard muriatic acid for lowering pH and Total Alkalinity. 31.45% strength.',
    usage: 'Used in acid demand tests or for general pH/TA reduction.',
    functions: ['LOWER_PH_TA'],
    formula: { 
        amountPerDrop: 12, 
        unitPerDrop: 'fl_oz',
        baseAmount: 16,
        baseUnit: 'fl_oz',
        perVolume: 10000,
        raisesBy: -0.2
    }
  },
   {
    id: 'generic_muriatic_acid_29',
    brand: 'Generic',
    productName: 'Muriatic 29%',
    category: 'Balancers',
    description: 'Liquid acid for lowering pH/TA. 29% strength.',
    usage: 'Handle with extreme care.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 17.4, baseUnit: 'fl_oz', perVolume: 10000, raisesBy: -0.2 }
  },
   {
    id: 'generic_muriatic_acid_28_3',
    brand: 'Generic',
    productName: 'Muriatic 28.3% - 18° Baumé',
    category: 'Balancers',
    description: 'Liquid acid for lowering pH/TA. 28.3% strength.',
    usage: 'Handle with extreme care.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 17.8, baseUnit: 'fl_oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'generic_muriatic_acid_15_7',
    brand: 'Generic',
    productName: 'Muriatic 15.7% - 10° Baumé',
    category: 'Balancers',
    description: 'Liquid acid for lowering pH/TA. 15.7% strength.',
    usage: 'Handle with extreme care.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 32.0, baseUnit: 'fl_oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'generic_muriatic_acid_14_5',
    brand: 'Generic',
    productName: 'Muriatic 14.5%',
    category: 'Balancers',
    description: 'Liquid acid for lowering pH/TA. 14.5% strength.',
    usage: 'Handle with extreme care.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 34.7, baseUnit: 'fl_oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'generic_soda_ash',
    brand: 'Generic',
    productName: 'Soda Ash (Sodium Carbonate)',
    category: 'Balancers',
    description: 'Standard soda ash for raising pH.',
    usage: 'Used in base demand tests or for general pH increase.',
    functions: ['RAISE_PH'],
    formula: { 
        amountPerDrop: 14, 
        unitPerDrop: 'oz',
        baseAmount: 6,
        baseUnit: 'oz',
        perVolume: 10000,
        raisesBy: 0.2
    }
  },
  {
    id: 'generic_chlorine_neutralizer',
    brand: 'Generic',
    productName: 'Chlorine Neutralizer (Sodium Thiosulfate)',
    category: 'Specialty',
    description: 'Granules used to rapidly lower dangerously high free chlorine levels.',
    usage: 'Add 2 oz per 10,000 gallons to reduce free chlorine by 1 ppm.',
    functions: ['LOWER_FC'],
    formula: { baseAmount: 2, baseUnit: 'oz', perVolume: 10000, raisesBy: -1 }
  },
  
  // --- O-ACE-SIS (Ace Hardware) ---
  {
    id: 'oacesis_cal_shock',
    brand: 'O-ACE-SIS',
    productName: 'Granule Cal-Shock',
    category: 'Shocks & Oxidizers',
    description: 'A calcium hypochlorite based shock (68%) for raising free chlorine levels and killing bacteria and algae. Also raises calcium hardness.',
    usage: 'Add 1 lb (16 oz) per 10,000 gallons for shock treatment. Raises FC by approx 5.8 ppm.',
    functions: ['ADD_FC', 'RAISE_CH'],
    formula: { amountPer10kGalToOnePpm: 2.76, unit: 'oz' }
  },
  {
    id: 'oacesis_multishock',
    brand: 'O-ACE-SIS',
    productName: 'Granule Multishock',
    category: 'Shocks & Oxidizers',
    description: 'A sodium dichlor based shock (58.5%) that raises free chlorine and cyanuric acid.',
    usage: 'Add 1 lb (16 oz) per 10,000 gallons for shock treatment. Raises FC by approx 4.8 ppm and CYA by 4.4 ppm.',
    functions: ['ADD_FC', 'RAISE_CYA'],
    formula: { amountPer10kGalToOnePpm: 3.33, unit: 'oz' }
  },
  {
    id: 'oacesis_trichlor_tablets',
    brand: 'O-ACE-SIS',
    productName: '3" Tablet Trichlor',
    category: 'Sanitizers',
    description: '99% Trichloro-s-Triazinetrione tablets for consistent chlorination. These are slow-dissolving and will add cyanuric acid and lower pH over time.',
    usage: 'Use 1-3 tablets per 10,000 gallons per week in a feeder or floater. Each 8 oz tablet adds approx 6.7 ppm FC and 4.1 ppm CYA.',
    // NOTE: While Trichlor is acidic and lowers pH, it is not used *for* that purpose. 
    // Removing 'LOWER_PH_TA' from functions prevents it from appearing as a primary pH adjuster, which is confusing.
    functions: ['ADD_FC', 'RAISE_CYA'],
    formula: { amountPer10kGalToOnePpm: 0.149, unit: 'tablets' }
  },
  {
    id: 'oacesis_brominating_tablets',
    brand: 'O-ACE-SIS',
    productName: 'Tablet Brominating Chemicals',
    category: 'Sanitizers',
    description: 'Bromine tablets (96% 1-Bromo-3-chloro-5,5-dimethylhydantoin) for sanitizing spas and hot tubs.',
    usage: 'Use 1-3 tablets per 300 gallons for spas. 1 oz raises Bromine by 4.0 ppm in 300 gallons.',
    functions: ['ADD_BROMINE'],
    formula: { baseAmount: 1, baseUnit: 'oz', perVolume: 300, raisesBy: 4.0 }
  },
  {
    id: 'oacesis_ph_plus',
    brand: 'O-ACE-SIS',
    productName: 'Granule pH Plus',
    category: 'Balancers',
    description: 'Sodium Carbonate (soda ash) used to raise the pH of pool water.',
    usage: 'Add 1 lb (16 oz) per 10,000 gallons to raise pH by approximately 0.2.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 16, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 }
  },
  {
    id: 'oacesis_alkalinity_increaser',
    brand: 'O-ACE-SIS',
    productName: 'Granule Alkalinity Increaser',
    category: 'Balancers',
    description: 'Sodium Bicarbonate (baking soda) used to raise the total alkalinity of pool water.',
    usage: 'Add 1.5 lbs (24 oz) per 10,000 gallons to raise total alkalinity by 10 ppm.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'oacesis_calcium_increaser',
    brand: 'O-ACE-SIS',
    productName: 'Granule Calcium Hardness Increaser',
    category: 'Balancers',
    description: 'Calcium Chloride used to raise the calcium hardness of pool water.',
    usage: 'Add 1.25 lbs (20 oz) per 10,000 gallons to raise hardness by 10 ppm.',
    functions: ['RAISE_CH'],
    formula: { baseAmount: 1.25, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'oacesis_stabilizer',
    brand: 'O-ACE-SIS',
    productName: 'Granule Chlorine Stabilizer',
    category: 'Balancers',
    description: 'Cyanuric Acid used to stabilize chlorine against degradation from sunlight.',
    usage: 'Add 1 lb (16 oz) per 4,000 gallons to raise CYA by 30 ppm.',
    functions: ['RAISE_CYA'],
    formula: { baseAmount: 1, baseUnit: 'lbs', perVolume: 4000, raisesBy: 30 }
  },
  {
    id: 'oacesis_liquid_algaecide',
    brand: 'O-ACE-SIS',
    productName: 'Liquid Algaecide',
    category: 'Algaecides & Clarifiers',
    description: 'A polyquat algaecide (10%) for preventing and treating algae growth in pools.',
    usage: 'Initial Dose: 3.7 oz per 1,000 gallons. Maintenance Dose: 1.3 oz per 1,000 gallons.',
    functions: ['ALGAECIDE'],
    formula: { baseAmount: 3.7, baseUnit: 'fl_oz', perVolume: 1000 }
  },
  {
    id: 'oacesis_floc_out',
    brand: 'O-ACE-SIS',
    productName: 'Super Floc Out Liquid Debris',
    category: 'Algaecides & Clarifiers',
    description: 'An aluminum chlorohydrate flocculant that drops suspended particles to the pool floor for easy vacuuming to waste.',
    usage: 'Add 8 oz per 10,000 gallons.',
    functions: ['CLARIFIER'],
    formula: { baseAmount: 8, baseUnit: 'fl_oz', perVolume: 10000 }
  },
  {
    id: 'oacesis_poly_brite_tabs',
    brand: 'O-ACE-SIS',
    productName: 'Poly-Brite Filter Tabs',
    category: 'Algaecides & Clarifiers',
    description: 'Clarifier tablets that help the filter remove fine particles from the water.',
    usage: 'Use one tablet per 15,000 gallons in the skimmer basket.',
    functions: ['CLARIFIER'],
    formula: { baseAmount: 1, baseUnit: 'tablets', perVolume: 15000 }
  },
  {
    id: 'oacesis_tile_cleaner',
    brand: 'O-ACE-SIS',
    productName: 'Liquid Tile and Vinyl Cleaner',
    category: 'Cleaners',
    description: 'A surface cleaner containing Ethylene Glycol Monobutyl Ether for removing grime and oil at the waterline.',
    usage: 'Apply directly to a cloth or brush to clean surfaces. Not for direct addition to pool water.',
    functions: [],
    formula: {}
  },
  {
    id: 'oacesis_stain_remover',
    brand: 'O-ACE-SIS',
    productName: 'Granule Stain Remover',
    category: 'Specialty',
    description: 'A citric acid based product for removing metal stains from pool surfaces. Lowers chlorine.',
    usage: 'Add 2 lbs per 15,000 gallons for widespread stains.',
    functions: ['STAIN_REMOVER'],
    formula: { baseAmount: 2, baseUnit: 'lbs', perVolume: 15000 }
  },
  {
    id: 'oacesis_chlorine_neutralizer',
    brand: 'O-ACE-SIS',
    productName: 'Granule Chlorine Neutralizer',
    category: 'Specialty',
    description: 'Sodium Thiosulfate granules used to rapidly lower high free chlorine levels.',
    usage: 'Add 2 oz per 10,000 gallons to reduce free chlorine by 1 ppm.',
    functions: ['LOWER_FC'],
    formula: { baseAmount: 2, baseUnit: 'oz', perVolume: 10000, raisesBy: -1 }
  },

  // ── Spa / Hot Tub Sanitizers ───────────────────────────────────────────────

  {
    id: 'leisure_time_spa56',
    brand: 'Leisure Time',
    productName: 'Spa 56 Granular Chlorine',
    category: 'Spa Sanitizers',
    description: 'Sodium dichlor (56%) granular chlorine specifically formulated for spas and hot tubs. Fast-dissolving and pH-neutral relative to other chlorine forms.',
    usage: 'Add 2 teaspoons (about 1 oz) per 300 gallons to raise FC by approximately 4 ppm. Pre-dissolve in water before adding.',
    functions: ['ADD_FC', 'ADD_BROMINE'],
    formula: { baseAmount: 1, baseUnit: 'oz', perVolume: 300, raisesBy: 4.0 }
  },
  {
    id: 'spaguard_brominating_tabs',
    brand: 'SpaGuard',
    productName: 'Brominating Tablets',
    category: 'Spa Sanitizers',
    description: 'Slow-dissolving 1" bromine tablets (BCDMH) for continuous spa sanitation. Bromine is more stable than chlorine at high spa temperatures.',
    usage: 'Use 1–3 tablets per 300 gallons per week in a floating dispenser. Maintain 3–5 ppm bromine.',
    functions: ['ADD_BROMINE'],
    formula: { baseAmount: 1, baseUnit: 'tablets', perVolume: 300, raisesBy: 3.0 }
  },
  {
    id: 'oacesis_brominating_tablets_spa',
    brand: 'O-ACE-SIS',
    productName: 'Brominating Tablets (Spa)',
    category: 'Spa Sanitizers',
    description: 'Bromine tablets (96% 1-Bromo-3-chloro-5,5-dimethylhydantoin) for sanitizing spas and hot tubs.',
    usage: 'Use 1–3 tablets per 300 gallons for spas. Raise Bromine to 3–5 ppm.',
    functions: ['ADD_BROMINE'],
    formula: { baseAmount: 1, baseUnit: 'oz', perVolume: 300, raisesBy: 4.0 }
  },
  {
    id: 'leisure_time_renew',
    brand: 'Leisure Time',
    productName: 'Renew Non-Chlorine Shock (MPS)',
    category: 'Spa Shocks & Oxidizers',
    description: 'Potassium monopersulfate (MPS) non-chlorine oxidizer. Destroys organic contaminants without raising chlorine or bromine levels. Allows re-entry in 15 minutes.',
    usage: 'Add 2 oz per 300 gallons after each use or weekly. Broadcast over spa water with jets running.',
    functions: ['ADD_FC'],
    formula: { baseAmount: 2, baseUnit: 'oz', perVolume: 300 }
  },
  {
    id: 'spaguard_enhanced_shock',
    brand: 'SpaGuard',
    productName: 'Enhanced Shock',
    category: 'Spa Shocks & Oxidizers',
    description: 'Cal-hypo based shock for spas. Rapidly raises sanitizer level to destroy bacteria, algae, and organic contaminants.',
    usage: 'Add 1 oz per 300 gallons to shock the spa. Pre-dissolve in a bucket of water.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 2.35, unit: 'oz' }
  },
  {
    id: 'dichlor_spa_granules',
    brand: 'Generic',
    productName: 'Dichlor Spa Granules (56%)',
    category: 'Spa Sanitizers',
    description: 'Sodium dichlor granules ideal for spas. Dissolves quickly, nearly pH-neutral, and adds a small amount of CYA per dose.',
    usage: 'Add 1 oz per 300 gallons to raise FC by approximately 4 ppm. Pre-dissolve before adding.',
    functions: ['ADD_FC', 'ADD_BROMINE'],
    formula: { baseAmount: 1, baseUnit: 'oz', perVolume: 300, raisesBy: 4.0 }
  },

  // ── Spa Balancers ──────────────────────────────────────────────────────────

  {
    id: 'spaguard_ph_lowers',
    brand: 'SpaGuard',
    productName: 'pH Lowers (Sodium Bisulfate)',
    category: 'Spa Balancers',
    description: 'Granular sodium bisulfate to lower pH and total alkalinity in spas.',
    usage: 'Add 1 oz per 300 gallons to lower pH by approximately 0.2. Pre-dissolve in spa water.',
    functions: ['LOWER_PH_TA'],
    formula: { baseAmount: 16, baseUnit: 'oz', perVolume: 10000, raisesBy: -0.2 }
  },
  {
    id: 'spaguard_ph_increases',
    brand: 'SpaGuard',
    productName: 'pH Increases (Sodium Carbonate)',
    category: 'Spa Balancers',
    description: 'Granular soda ash to raise pH in spas. Hot tub pH tends to drift upward — use sparingly.',
    usage: 'Add small amounts and retest. 1 oz per 300 gallons raises pH by approximately 0.2.',
    functions: ['RAISE_PH'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 }
  },
  {
    id: 'spaguard_total_alkalinity_increases',
    brand: 'SpaGuard',
    productName: 'Total Alkalinity Increases (Sodium Bicarbonate)',
    category: 'Spa Balancers',
    description: 'Sodium bicarbonate to raise total alkalinity in spas. Buffering TA stabilizes pH drift.',
    usage: 'Add 1.5 oz per 300 gallons to raise TA by approximately 10 ppm.',
    functions: ['RAISE_TA'],
    formula: { baseAmount: 1.5, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },
  {
    id: 'leisure_time_ph_balance',
    brand: 'Leisure Time',
    productName: 'pH Balance Plus',
    category: 'Spa Balancers',
    description: 'A combined pH and alkalinity balancer for spas. Raises both pH and total alkalinity when they are both low.',
    usage: 'Add 1 oz per 300 gallons. Retest after 30 minutes.',
    functions: ['RAISE_PH', 'RAISE_TA'],
    formula: { baseAmount: 6, baseUnit: 'oz', perVolume: 10000, raisesBy: 0.2 }
  },
  {
    id: 'leisure_time_calcium_booster',
    brand: 'Leisure Time',
    productName: 'Calcium Booster (Calcium Chloride)',
    category: 'Spa Balancers',
    description: 'Calcium chloride to raise calcium hardness in spas and protect acrylic shells from aggressive low-hardness water.',
    usage: 'Add 1.25 oz per 300 gallons to raise CH by approximately 10 ppm.',
    functions: ['RAISE_CH'],
    formula: { baseAmount: 1.25, baseUnit: 'lbs', perVolume: 10000, raisesBy: 10 }
  },

  // ── Spa Specialty Products ─────────────────────────────────────────────────

  {
    id: 'leisure_time_foam_down',
    brand: 'Leisure Time',
    productName: 'Foam Down',
    category: 'Spa Specialty',
    description: 'Silicone-based defoamer that eliminates foam caused by body oils, lotions, soaps, and detergent residue in spa water.',
    usage: 'Add 1 capful (1 fl oz) directly into spa water. Works immediately. Address root cause: drain and refill if foaming persists.',
    functions: [],
    formula: {}
  },
  {
    id: 'leisure_time_bright_clear',
    brand: 'Leisure Time',
    productName: 'Bright & Clear Clarifier',
    category: 'Spa Specialty',
    description: 'Polymer clarifier that coagulates fine particles so the filter can remove them, restoring water clarity to hazy spa water.',
    usage: 'Add 1 oz per 250 gallons weekly or when water appears cloudy.',
    functions: [],
    formula: { baseAmount: 1, baseUnit: 'fl_oz', perVolume: 250 }
  },
  {
    id: 'leisure_time_enzyme',
    brand: 'Leisure Time',
    productName: 'Enzyme (Natural Spa Enzyme)',
    category: 'Spa Specialty',
    description: 'Enzymes that break down non-living organic contaminants like body oils, lotions, and cosmetics. Reduces the demand on sanitizers and prevents scum lines.',
    usage: 'Add 1 oz per 250 gallons weekly. Pour near the jets.',
    functions: [],
    formula: { baseAmount: 1, baseUnit: 'fl_oz', perVolume: 250 }
  },
  {
    id: 'ahh_some_line_flush',
    brand: 'Ahh-Some',
    productName: 'Hot Tub & Jetted Bath Cleaner (Line Flush)',
    category: 'Spa Specialty',
    description: 'Biofilm purge formula that removes accumulated biofilm, oils, and scale from spa plumbing lines. Use before each drain and refill.',
    usage: 'Add 1/4 teaspoon per 100 gallons. Run jets on high for 30 minutes, then drain spa.',
    functions: [],
    formula: {}
  },
  {
    id: 'natural_chemistry_spa_perfect',
    brand: 'Natural Chemistry',
    productName: 'Spa Perfect (Enzyme)',
    category: 'Spa Specialty',
    description: 'Continuous enzyme treatment that breaks down oils and organics. Reduces waterline buildup, foaming, and chlorine/bromine demand.',
    usage: 'Add 1 oz per 250 gallons weekly.',
    functions: [],
    formula: { baseAmount: 1, baseUnit: 'fl_oz', perVolume: 250 }
  },
  {
    id: 'spaguard_stain_scale_control',
    brand: 'SpaGuard',
    productName: 'Stain & Scale Control',
    category: 'Spa Specialty',
    description: 'Chelating agent that prevents calcium scale deposits and metal staining on spa surfaces and equipment. Important in hard-water areas.',
    usage: 'Add 1 oz per 300 gallons weekly or when starting with fresh water.',
    functions: [],
    formula: { baseAmount: 1, baseUnit: 'fl_oz', perVolume: 300 }
  },

  // ── Additional Pool Brands ─────────────────────────────────────────────────

  {
    id: 'intheswim_cal_hypo_shock',
    brand: 'In The Swim',
    productName: 'Cal Hypo Pool Shock (73%)',
    category: 'Shocks & Oxidizers',
    description: '73% calcium hypochlorite shock. High-strength granular shock for rapid FC increase and algae elimination.',
    usage: 'Add 1 lb per 10,000 gallons. Pre-dissolve in a bucket before adding. Raises FC approximately 7 ppm.',
    functions: ['ADD_FC', 'RAISE_CH'],
    formula: { amountPer10kGalToOnePpm: 2.19, unit: 'oz' }
  },
  {
    id: 'intheswim_dichlor_shock',
    brand: 'In The Swim',
    productName: 'Dichlor Pool Shock (99%)',
    category: 'Shocks & Oxidizers',
    description: '99% sodium dichlor shock. Stabilized chlorine that dissolves fast and adds CYA. Good for pools that need stabilizer.',
    usage: 'Add 1 lb per 10,000 gallons. Raises approx 8 ppm FC and 6 ppm CYA.',
    functions: ['ADD_FC', 'RAISE_CYA'],
    formula: { amountPer10kGalToOnePpm: 2.0, unit: 'oz' }
  },
  {
    id: 'intheswim_liquid_chlorine',
    brand: 'In The Swim',
    productName: 'Liquid Chlorine (10%)',
    category: 'Sanitizers',
    description: '10% sodium hypochlorite liquid chlorine for daily pool chlorination.',
    usage: 'Add 10 fl oz per 10,000 gallons to raise FC by 1 ppm.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 10.4, unit: 'fl_oz' }
  },
  {
    id: 'bioguard_burnout_35',
    brand: 'BioGuard',
    productName: 'Burnout 35 (Cal-Hypo Shock)',
    category: 'Shocks & Oxidizers',
    description: '35% calcium hypochlorite shock packet. Convenient single-dose format.',
    usage: 'One 1 lb bag per 10,000 gallons. Raises approximately 3.5 ppm FC.',
    functions: ['ADD_FC'],
    formula: { amountPer10kGalToOnePpm: 4.57, unit: 'oz' }
  },
  {
    id: 'natural_chemistry_pool_perfect',
    brand: 'Natural Chemistry',
    productName: 'Pool Perfect + PHOSfree',
    category: 'Specialty',
    description: 'Enzyme + phosphate remover combination. Enzymes break down non-living organics; PHOSfree removes phosphates that feed algae.',
    usage: 'Add 1 liter (33 fl oz) per 30,000 gallons monthly, or per label rates.',
    functions: [],
    formula: {}
  },
  {
    id: 'clorox_pool_shock_xtra_blue',
    brand: 'Clorox Pool&Spa',
    productName: 'Super Water Clarifier',
    category: 'Algaecides & Clarifiers',
    description: 'Polymer clarifier that quickly clears cloudy pool water by coagulating fine suspended particles.',
    usage: 'Add 2 fl oz per 5,000 gallons to the skimmer or directly to pool.',
    functions: [],
    formula: { baseAmount: 2, baseUnit: 'fl_oz', perVolume: 5000 }
  }
];
